import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, Outlet } from 'react-router-dom'
import { NumberParam, useQueryParam } from 'use-query-params'
import { toast } from 'react-hot-toast'
import { useCampaigns } from '@/context/campaigns'
import { useCreditBalance } from '@/hooks/useCreditBalance'
import { CampaignPageHeader, CampaignQuickNav } from '../components'
import { StoryBeat, CampaignTarget } from '@/types/campaigns'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles, Layers, Plus, X, Zap, Rocket, Star, Briefcase, Flame, Palette, Lightbulb } from 'lucide-react'
import { useSocketContext } from '@/context'
import { TemplateGroup } from './ChannelTemplateSelector/types'

/* eslint-disable react-refresh/only-export-components -- context module */

// Icon mapping for template groups
const ICON_MAP: Record<string, any> = {
    zap: Zap,
    rocket: Rocket,
    star: Star,
    briefcase: Briefcase,
    flame: Flame,
    palette: Palette,
    lightbulb: Lightbulb,
    layers: Layers,
}

interface BeatPageContextType {
    beats: StoryBeat[]
    currentBeatNumber: number
    setCurrentBeatNumber: (num: number) => void
    loading: boolean
    loadBeats: () => Promise<StoryBeat[]>
    primaryTarget: CampaignTarget | null
    campaignAssets: any[]
    visualConceptsComplete: number
    channelsAssignedCount: number
    totalTopics: number
    projectedContent: number
    headerProps: {
        title: string
        subtitle: string
        onBack: () => void
        onContinue?: () => void
        continueLabel?: string
        continueDisabled?: boolean
        headerActions?: React.ReactNode | null
    }
    setHeaderProps: (props: any) => void
    sidebarActions: React.ReactNode
    setSidebarActions: (actions: React.ReactNode) => void
    handleGenerateVisualsForBeat: (beatId: string) => Promise<void>
    handleApplyAllChannelsForBeat: (beatId: string, templateIds: string[]) => Promise<void>
    templateGroups: TemplateGroup[]
    setTemplateGroups: (groups: TemplateGroup[]) => void
    previewImageUrl: string | null
    setPreviewImageUrl: (url: string | null) => void
}

const BeatPageContext = createContext<BeatPageContextType | undefined>(undefined)

export const useBeatPage = () => {
    const context = useContext(BeatPageContext)
    if (!context) throw new Error('useBeatPage must be used within a BeatPageLayout')
    return context
}

export const BeatPageLayout: React.FC = () => {
    const { campaignId } = useParams<{ campaignId: string }>()
    const navigate = useNavigate()
    const { currentCampaign } = useCampaigns()
    const { socket } = useSocketContext() as any
    useCreditBalance()
    
    const [beatParam, setBeatParam] = useQueryParam('beat', NumberParam)
    
    // 🆕 Initialize from URL immediately, or default to 1
    const [currentBeatNumber, setCurrentBeatNumber] = useState<number>(() => {
        const params = new URLSearchParams(window.location.search)
        const beat = params.get('beat')
        return beat ? parseInt(beat) : 1
    })

    const [beats, setBeats] = useState<StoryBeat[]>([])
    const [loading, setLoading] = useState(true)
    const [primaryTarget, setPrimaryTarget] = useState<CampaignTarget | null>(null)
    const [campaignAssets, setCampaignAssets] = useState<any[]>([])
    const [sidebarActions, setSidebarActions] = useState<React.ReactNode>(null)
    const [templateGroups, setTemplateGroups] = useState<TemplateGroup[]>([])
    const [showQuickAssignModal, setShowQuickAssignModal] = useState(false)
    const [quickAssignBeatId, setQuickAssignBeatId] = useState<string | null>(null)
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)

    // 🆕 Load Template Groups from localStorage
    useEffect(() => {
        if (campaignId && campaignId !== 'undefined') {
            const saved = localStorage.getItem(`templateGroups_${campaignId}`)
            if (saved) {
                try {
                    setTemplateGroups(JSON.parse(saved))
                } catch (e) {
                    console.error('Failed to parse template groups:', e)
                }
            }
        }
    }, [campaignId])

    // 🆕 Sync Template Groups to localStorage
    useEffect(() => {
        if (campaignId && campaignId !== 'undefined' && templateGroups.length > 0) {
            localStorage.setItem(`templateGroups_${campaignId}`, JSON.stringify(templateGroups))
        }
    }, [campaignId, templateGroups])
    const [headerProps, setHeaderProps] = useState({
        title: 'Campaign Builder',
        subtitle: '',
        onBack: () => navigate('/dashboard/campaigns'),
        onContinue: undefined as (() => void) | undefined,
        continueLabel: 'Continue →',
        continueDisabled: false,
        headerActions: null as React.ReactNode | null,
    })

    // 🆕 URL → State: Only update state when URL param changes
    useEffect(() => {
        if (beatParam !== undefined && beatParam !== null && beatParam !== currentBeatNumber) {
            console.log(`🔄 [BeatPageLayout] URL changed to beat ${beatParam}, updating state`)
            setCurrentBeatNumber(beatParam)
        }
    }, [beatParam, currentBeatNumber])

    // 🆕 State → URL: Only update URL when state changes (and we have beats loaded)
    useEffect(() => {
        if (currentBeatNumber && currentBeatNumber !== beatParam && beats.length > 0) {
            console.log(`🔄 [BeatPageLayout] State is beat ${currentBeatNumber}, updating URL`)
            setBeatParam(currentBeatNumber, 'replaceIn')
        }
    }, [currentBeatNumber, beatParam, beats.length, setBeatParam])

    const loadBeats = useCallback(async () => {
        if (!campaignId || campaignId === 'undefined' || campaignId.length !== 24) {
            console.warn(`⚠️ [BeatPageLayout] Cannot load beats - invalid campaignId:`, campaignId)
            setLoading(false)
            return []
        }
        try {
            const response = await fetch(`/api/campaigns/${campaignId}/beats`)
            if (!response.ok) throw new Error('Failed to fetch beats')
            const result = await response.json()
            
            const assetsRes = await fetch(`/api/campaigns/${campaignId}/assets`)
            if (assetsRes.ok) {
                const assetsData = await assetsRes.json()
                setCampaignAssets(assetsData.assets || [])
            }

            if (Array.isArray(result) && result.length > 0) {
                setBeats(result)
                
                // 🆕 Only fallback to first beat if the current one doesn't exist in the results
                const beatExists = result.find(b => b.beat_number === currentBeatNumber)
                if (!beatExists) {
                    console.log(`⚠️ [BeatPageLayout] Beat ${currentBeatNumber} not found in results, falling back to Beat ${result[0].beat_number}`)
                    setCurrentBeatNumber(result[0].beat_number)
                }
                
                setLoading(false)
                return result
            }
            setLoading(false)
            return []
        } catch (error) {
            console.error('Failed to load beats:', error)
            setLoading(false)
            return []
        }
    }, [campaignId, currentBeatNumber]) // Keep currentBeatNumber to re-check if beats change

    const loadTarget = useCallback(async () => {
        if (!campaignId || campaignId === 'undefined' || campaignId.length !== 24) return
        try {
            const response = await fetch(`/api/campaigns/${campaignId}/targets`)
            if (!response.ok) throw new Error('Failed to fetch targets')
            const result = await response.json()
            if (Array.isArray(result) && result.length > 0) {
                const primary = result.find((t: any) => t.type === 'primary') || result[0]
                setPrimaryTarget(primary)
            }
        } catch (error) {
            console.error('Failed to load target profile', error)
        }
    }, [campaignId])

    useEffect(() => {
        if (campaignId && campaignId !== 'undefined' && campaignId.length === 24) {
            loadBeats()
            loadTarget()
        }
    }, [campaignId, loadBeats, loadTarget])

    // 🆕 WebSocket listeners to keep campaignAssets in sync
    useEffect(() => {
        if (!socket || !campaignId) return

        const handleAssetUpdate = (data: any) => {
            // Refresh assets if the update is for this campaign
            if (data.campaignId === campaignId) {
                console.log('🔄 [BeatPageLayout] WebSocket event received, refreshing assets:', data)
                loadBeats()
            }
        }

        socket.on('post-generated', handleAssetUpdate)
        socket.on('image-generated', handleAssetUpdate)
        socket.on('asset-updated', handleAssetUpdate)
        socket.on('beat_updated', handleAssetUpdate)

        return () => {
            socket.off('post-generated', handleAssetUpdate)
            socket.off('image-generated', handleAssetUpdate)
            socket.off('asset-updated', handleAssetUpdate)
            socket.off('beat_updated', handleAssetUpdate)
        }
    }, [socket, campaignId, loadBeats])

    // Stats calculations
    const visualConceptsComplete = useMemo(() => {
        return beats.reduce((count, beat) => {
            return count + (beat.topic_bank?.topics?.filter((t: any) => t.is_selected && (t.sample_image_url || (t.visual_prompt && t.visual_prompt.trim() !== ''))).length || 0)
        }, 0)
    }, [beats])

    const channelsAssignedCount = useMemo(() => {
        return beats.reduce((count, beat) => {
            return count + (beat.topic_bank?.topics?.filter((t: any) => t.is_selected && t.assigned_templates && t.assigned_templates.length > 0).length || 0)
        }, 0)
    }, [beats])

    const totalTopics = useMemo(() => {
        return beats.reduce((count, beat) => {
            return count + (beat.topic_bank?.topics?.filter((t: any) => t.is_selected).length || 0)
        }, 0)
    }, [beats])

    const projectedContent = useMemo(() => {
        return beats.reduce((sum, beat) => {
            const selectedTopicsInBeat = beat.topic_bank?.topics?.filter((t: any) => t.is_selected) || []
            return sum + selectedTopicsInBeat.reduce((tSum: number, topic: any) => {
                const channelCount = topic.assigned_templates?.length || (currentCampaign?.config?.selected_channels?.length || 1)
                return tSum + channelCount
            }, 0)
        }, 0)
    }, [beats, currentCampaign])

    const handleGenerateVisualsForBeat = useCallback(async (beatId: string) => {
        const toastId = toast.loading('Generating visual concepts...')
        try {
            const response = await fetch(`/api/campaigns/${campaignId}/topics/visual-prompts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ beatId, scope: 'current' }),
            })
            const result = await response.json()
            if (response.ok) {
                toast.success(result.message || 'Generated visual concepts!', { id: toastId })
                loadBeats()
            } else {
                toast.error(result.error || 'Failed to generate', { id: toastId })
            }
        } catch (err) {
            toast.error('Failed to generate visuals', { id: toastId })
        }
    }, [campaignId, loadBeats])

    const handleApplyAllChannelsForBeat = useCallback(async (beatId: string, templateIds: string[]) => {
        const beat = beats.find(b => b._id === beatId)
        if (!beat) return
        
        const toastId = toast.loading(`Assigning channels to all topics...`)
        
        try {
            const selectedTopics = beat.topic_bank?.topics?.filter((t: any) => t.is_selected) || []
            if (selectedTopics.length === 0) {
                toast.error('No topics selected in this beat', { id: toastId })
                return
            }

            await Promise.all(selectedTopics.map((t: any) => 
                fetch(`/api/campaigns/${campaignId}/beats/${beatId}/topics/${t._id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ assigned_templates: templateIds }),
                })
            ))
            
            toast.success('Channels assigned to all topics!', { id: toastId, icon: '✅' })
            loadBeats()
        } catch (err) {
            console.error('Failed to apply channels:', err)
            toast.error('Failed to assign channels', { id: toastId })
        }
    }, [campaignId, beats, loadBeats])

    const contextValue = useMemo(() => ({
        beats,
        currentBeatNumber,
        setCurrentBeatNumber,
        loading,
        loadBeats,
        primaryTarget,
        campaignAssets,
        visualConceptsComplete,
        channelsAssignedCount,
        totalTopics,
        projectedContent,
        headerProps,
        setHeaderProps,
        sidebarActions,
        setSidebarActions,
        handleGenerateVisualsForBeat,
        handleApplyAllChannelsForBeat,
        templateGroups,
        setTemplateGroups,
        previewImageUrl,
        setPreviewImageUrl
    }), [beats, currentBeatNumber, loading, loadBeats, primaryTarget, campaignAssets, visualConceptsComplete, channelsAssignedCount, totalTopics, projectedContent, headerProps, sidebarActions, templateGroups, previewImageUrl, handleGenerateVisualsForBeat, handleApplyAllChannelsForBeat])

    // 🚨 Guard: Invalid campaignId
    if (!campaignId || campaignId === 'undefined' || campaignId.length !== 24) {
        return (
            <div className='flex items-center justify-center h-screen'>
                <div className='text-center'>
                    <p className='text-red-600 font-bold mb-2'>Error: Invalid Campaign ID</p>
                    <p className='text-gray-600 mb-4'>Campaign ID: {campaignId || 'missing'}</p>
                    <button 
                        onClick={() => navigate('/dashboard/campaigns')}
                        className='px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700'
                    >
                        Back to Campaigns
                    </button>
                </div>
            </div>
        )
    }

    if (loading && beats.length === 0)
        return <div className='flex items-center justify-center h-screen'>Loading Campaign Data...</div>

    const currentPage = location.pathname.split('/').pop() || 'topics'

    return (
        <BeatPageContext.Provider value={contextValue}>
            <div className='min-h-screen bg-gray-50'>
                <CampaignPageHeader
                    title={headerProps.title}
                    subtitle={headerProps.subtitle}
                    onBack={headerProps.onBack}
                    onContinue={headerProps.onContinue}
                    continueLabel={headerProps.continueLabel}
                    continueDisabled={headerProps.continueDisabled}
                >
                    {headerProps.headerActions}
                </CampaignPageHeader>
                <CampaignQuickNav currentPage={currentPage === 'visual-identity' ? 'visual-identity' : 'topics'} />
                
                <div className='flex'>
                    {/* Persistent Sidebar */}
                    <aside className='w-80 bg-white border-r border-gray-200 h-[calc(100vh-140px)] overflow-y-auto sticky top-[140px] flex-shrink-0 scrollbar-hide'>
                        <div className='p-4 border-b border-gray-100'>
                            {/* Stats Card */}
                            <div className='bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100 rounded-xl p-4 space-y-3'>
                                <div className='text-center pb-3 border-b border-violet-200'>
                                    <div className='text-3xl font-black text-purple-600 leading-none'>{projectedContent}</div>
                                    <p className='text-[10px] text-gray-500 uppercase font-black tracking-tighter mt-1'>Total Content Pieces</p>
                                </div>
                                <div className='space-y-2'>
                                    <div className='bg-white rounded-lg p-2 border border-violet-100'>
                                        <div className='flex items-center justify-between mb-1'>
                                            <span className='text-[10px] font-bold text-gray-600 uppercase tracking-wide'>Visual Concepts</span>
                                            <span className='text-xs font-black text-violet-600'>{visualConceptsComplete} / {totalTopics}</span>
                                        </div>
                                        <div className='w-full bg-gray-100 rounded-full h-1.5'>
                                            <div className='bg-gradient-to-r from-violet-500 to-purple-500 h-1.5 rounded-full transition-all' style={{ width: `${totalTopics > 0 ? (visualConceptsComplete / totalTopics) * 100 : 0}%` }} />
                                        </div>
                                        {visualConceptsComplete < totalTopics && (
                                            <p className='text-[9px] text-amber-600 font-medium mt-1'>⚠️ {totalTopics - visualConceptsComplete} missing</p>
                                        )}
                                    </div>
                                    <div className='bg-white rounded-lg p-2 border border-violet-100'>
                                        <div className='flex items-center justify-between mb-1'>
                                            <span className='text-[10px] font-bold text-gray-600 uppercase tracking-wide'>Channels Assigned</span>
                                            <span className='text-xs font-black text-indigo-600'>{channelsAssignedCount} / {totalTopics}</span>
                                        </div>
                                        <div className='w-full bg-gray-100 rounded-full h-1.5'>
                                            <div className='bg-gradient-to-r from-indigo-500 to-blue-500 h-1.5 rounded-full transition-all' style={{ width: `${totalTopics > 0 ? (channelsAssignedCount / totalTopics) * 100 : 0}%` }} />
                                        </div>
                                        {channelsAssignedCount < totalTopics && (
                                            <p className='text-[9px] text-amber-600 font-medium mt-1'>⚠️ {totalTopics - channelsAssignedCount} missing</p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Sidebar Actions Area */}
                                {sidebarActions && (
                                    <div className='pt-4 mt-4 border-t border-violet-100'>
                                        {sidebarActions}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Beat List */}
                        <div className='p-2 space-y-1'>
                            <h3 className='text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-3 px-2 mt-4'>
                                Story Beats
                            </h3>
                            {beats.map((beat) => {
                                const isActive = beat.beat_number === currentBeatNumber
                                const selectedTopics = (beat.topic_bank?.topics?.filter((t: any) => t.is_selected) || []) as any[]
                                const topicCount = selectedTopics.length
                                
                                // Calculate per-beat completion
                                const beatVisualsComplete = selectedTopics.filter((t: any) => t.sample_image_url || (t.visual_prompt && t.visual_prompt.trim() !== '')).length
                                const beatChannelsComplete = selectedTopics.filter((t: any) => t.assigned_templates && t.assigned_templates.length > 0).length
                                
                                const visualsMissing = topicCount - beatVisualsComplete
                                const channelsMissing = topicCount - beatChannelsComplete

                                return (
                                    <div
                                        key={beat._id}
                                        onClick={() => setCurrentBeatNumber(beat.beat_number)}
                                        className={`w-full text-left p-3 rounded-xl transition-all cursor-pointer min-h-[140px] flex flex-col ${
                                            isActive ? 'bg-white shadow-md border-2 border-violet-300' : 'bg-white/50 border border-gray-100 hover:bg-white hover:shadow-sm'
                                        }`}
                                    >
                                        <div className='flex items-center justify-between mb-2'>
                                            <div className='flex items-center gap-2'>
                                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isActive ? 'border-violet-500 bg-violet-500' : 'border-gray-300'}`}>
                                                    {isActive && <div className='w-1.5 h-1.5 rounded-full bg-white' />}
                                                </div>
                                                <span className='text-sm font-bold text-gray-700'>Beat {beat.beat_number}</span>
                                            </div>
                                            <span className='text-[10px] font-bold text-gray-400'>{topicCount} topics</span>
                                        </div>
                                        <p className='text-xs text-gray-600 mb-2 line-clamp-1 font-medium'>{beat.beat_name || beat.title}</p>
                                        
                                        {topicCount > 0 && (
                                            <div className='space-y-2.5 pt-3 mt-2 border-t border-gray-50 flex-1 flex flex-col justify-between'>
                                                <div className='space-y-1.5'>
                                                    <div className='flex items-center gap-1.5'>
                                                        {beatVisualsComplete === topicCount ? (
                                                            <><div className='w-1 h-1 rounded-full bg-emerald-500' /><span className='text-[9px] text-emerald-600 font-bold uppercase tracking-tight'>✓ Visuals Ready</span></>
                                                        ) : (
                                                            <><div className='w-1 h-1 rounded-full bg-amber-500' /><span className='text-[9px] text-amber-600 font-bold uppercase tracking-tight'>{visualsMissing} Visual{visualsMissing !== 1 ? 's' : ''} Missing</span></>
                                                        )}
                                                    </div>
                                                    <div className='flex items-center gap-1.5'>
                                                        {beatChannelsComplete === topicCount ? (
                                                            <><div className='w-1 h-1 rounded-full bg-emerald-500' /><span className='text-[9px] text-emerald-600 font-bold uppercase tracking-tight'>✓ Channels Set</span></>
                                                        ) : (
                                                            <><div className='w-1 h-1 rounded-full bg-amber-500' /><span className='text-[9px] text-amber-600 font-bold uppercase tracking-tight'>{channelsMissing} Channel{channelsMissing !== 1 ? 's' : ''} Missing</span></>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className='grid grid-cols-2 gap-2 pt-1'>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleGenerateVisualsForBeat(beat._id)
                                                        }}
                                                        className='flex items-center justify-center gap-1 py-1.5 px-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-all border border-amber-100 group/btn'
                                                        title='Generate visual prompts for all selected topics in this beat (runs in background)'
                                                    >
                                                        <Sparkles size={10} className='group-hover/btn:scale-110 transition-transform' />
                                                        <span className='text-[8px] font-black uppercase tracking-wider'>Visuals</span>
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            if (templateGroups.length > 0) {
                                                                setQuickAssignBeatId(beat._id)
                                                                setShowQuickAssignModal(true)
                                                            } else {
                                                                // Fallback if no groups: use campaign defaults
                                                                toast.error('No template groups found. Create one in the settings panel to use quick assign.')
                                                            }
                                                        }}
                                                        className='flex items-center justify-center gap-1 py-1.5 px-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-all border border-indigo-100 group/btn'
                                                        title='Apply template group to all selected topics in this beat'
                                                    >
                                                        <Layers size={10} className='group-hover/btn:scale-110 transition-transform' />
                                                        <span className='text-[8px] font-black uppercase tracking-wider'>Channels</span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Spacer for cards without topics to maintain consistent height */}
                                        {topicCount === 0 && (
                                            <div className='flex-1' />
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className='flex-1 min-w-0 bg-gray-50/50 min-h-[calc(100vh-140px)]'>
                        <Outlet />
                    </main>
                </div>

                {previewImageUrl && (
                    <div className='fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl' onClick={() => setPreviewImageUrl(null)}>
                        <img src={previewImageUrl} className='max-w-full max-h-full object-contain rounded-2xl shadow-2xl' alt='Preview' />
                    </div>
                )}

                {/* 🆕 Quick Assign Modal */}
                <AnimatePresence>
                    {showQuickAssignModal && (
                        <div className='fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm' onClick={() => setShowQuickAssignModal(false)}>
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                className='bg-white rounded-[2rem] shadow-2xl border-2 border-violet-100 w-full max-w-md overflow-hidden'
                                onClick={e => e.stopPropagation()}
                            >
                                <div className='p-6 border-b border-gray-100 bg-gradient-to-br from-violet-50 to-purple-50'>
                                    <div className='flex items-center justify-between mb-1'>
                                        <h3 className='text-lg font-black text-gray-900 uppercase tracking-tight'>Quick Assign Channels</h3>
                                        <button onClick={() => setShowQuickAssignModal(false)} className='p-2 hover:bg-white rounded-xl transition-all'><X size={20} className='text-gray-400' /></button>
                                    </div>
                                    <p className='text-xs text-gray-500 font-bold uppercase tracking-widest opacity-60'>Select a template group to apply to all topics in Beat {beats.find(b => b._id === quickAssignBeatId)?.beat_number}</p>
                                </div>
                                
                                <div className='p-6 space-y-3 max-h-[400px] overflow-y-auto'>
                                    {templateGroups.map(group => {
                                        const IconComponent = ICON_MAP[group.icon || 'zap'] || Zap
                                        return (
                                            <button
                                                key={group.id}
                                                onClick={() => {
                                                    if (quickAssignBeatId) {
                                                        handleApplyAllChannelsForBeat(quickAssignBeatId, group.templateIds)
                                                        setShowQuickAssignModal(false)
                                                    }
                                                }}
                                                className='w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-50 hover:border-violet-200 hover:bg-violet-50/30 transition-all group/item text-left'
                                            >
                                                <div className='w-10 h-10 rounded-xl bg-white border-2 border-gray-100 flex items-center justify-center text-violet-600 shadow-sm group-hover/item:scale-110 transition-transform'>
                                                    <IconComponent size={20} strokeWidth={2.5} />
                                                </div>
                                                <div className='flex-1 min-w-0'>
                                                    <h4 className='text-sm font-black text-gray-900 uppercase tracking-tight'>{group.name}</h4>
                                                    <p className='text-[10px] text-gray-500 font-bold uppercase tracking-widest opacity-60'>{group.templateIds.length} Templates Assigned</p>
                                                </div>
                                                <div className='w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 opacity-0 group-hover/item:opacity-100 transition-opacity'>
                                                    <Plus size={16} strokeWidth={3} />
                                                </div>
                                            </button>
                                        )
                                    })}
                                </div>

                                <div className='p-6 bg-gray-50 border-t border-gray-100 flex items-center justify-between'>
                                    <p className='text-[10px] text-gray-400 font-black uppercase tracking-widest'>Manage groups in settings panel</p>
                                    <button 
                                        onClick={() => setShowQuickAssignModal(false)}
                                        className='px-4 py-2 text-[10px] font-black text-gray-500 uppercase tracking-widest hover:text-gray-700'
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </div>
        </BeatPageContext.Provider>
    )
}
